// designSystem.js
import { createContext, useContext, useState, useEffect } from 'react';

// Fargepaletten - utvidet og konsistent
export const colors = {
  // Primærfarger
  primary: {
    gradient: "bg-gradient-to-r from-blue-600 to-indigo-600",
    gradientHover: "hover:from-blue-700 hover:to-indigo-700",
    light: "bg-blue-50 dark:bg-blue-900/20",
    text: "text-blue-600 dark:text-blue-400",
    border: "border-blue-200 dark:border-blue-800/30",
    focusRing: "focus:ring-blue-500 dark:focus:ring-blue-400",
  },
  
  // UI-tilstander
  danger: {
    bg: "bg-red-500",
    bgHover: "hover:bg-red-600",
    light: "bg-red-50 dark:bg-red-900/20",
    text: "text-red-600 dark:text-red-400",
    border: "border-red-200 dark:border-red-800/30",
  },
  success: {
    bg: "bg-green-500",
    bgHover: "hover:bg-green-600",
    light: "bg-green-50 dark:bg-green-900/20",
    text: "text-green-600 dark:text-green-400",
    border: "border-green-200 dark:border-green-800/30",
  },
  warning: {
    bg: "bg-amber-500",
    bgHover: "hover:bg-amber-600",
    light: "bg-amber-50 dark:bg-amber-900/20",
    text: "text-amber-600 dark:text-amber-400",
    border: "border-amber-200 dark:border-amber-800/30",
  },
  
  // Nøytrale farger
  neutral: {
    bg: "bg-white dark:bg-neutral-900",
    bgSecondary: "bg-gray-50 dark:bg-neutral-800",
    bgSubtle: "bg-white/90 dark:bg-neutral-900/90",
    bgHover: "hover:bg-gray-50 dark:hover:bg-neutral-800",
    border: "border-gray-200 dark:border-neutral-700",
    textPrimary: "text-gray-900 dark:text-neutral-100",
    textSecondary: "text-gray-600 dark:text-neutral-400",
    textTertiary: "text-gray-500 dark:text-neutral-500",
    textQuaternary: "text-gray-400 dark:text-neutral-500",
    textQuinary: "text-gray-300 dark:text-neutral-500",
    textPlaceholder: "placeholder-gray-400 dark:placeholder-neutral-600",
  },
  
  // Status-indikatorer
  status: {
    active: "bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300",
    completed: "bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-300",
    warning: "bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-300",
    error: "bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-300",
    recording: "bg-red-500", // Spesifikt for opptaksfunksjonalitet
  },

  // Språkindikatorer for forskjellige landskoder
  language: {
    no: "bg-red-50 text-red-600 dark:bg-red-900/50 dark:text-red-300",
    en: "bg-blue-50 text-blue-600 dark:bg-blue-900/50 dark:text-blue-300",
    other: "bg-gray-50 text-gray-600 dark:bg-gray-800 dark:text-gray-300",
  }
};

// Konsistente spacing-verdier
export const spacing = {
  xs: "p-1",
  sm: "p-2",
  md: "p-3",
  lg: "p-4",
  xl: "p-6",
  
  // Padding med forskjellige retninger
  px: {
    xs: "px-1",
    sm: "px-2",
    md: "px-3",
    lg: "px-4",
    xl: "px-6",
  },
  py: {
    xs: "py-1",
    sm: "py-2",
    md: "py-3",
    lg: "py-4",
    xl: "py-6",
  },
  pt: {
    xs: "pt-1",
    sm: "pt-2",
    md: "pt-3",
    lg: "pt-4",
    xl: "pt-6",
  },
  pb: {
    xs: "pb-1",
    sm: "pb-2",
    md: "pb-3",
    lg: "pb-4",
    xl: "pb-6",
  },
  
  // Margin med forskjellige retninger
  m: {
    xs: "m-1",
    sm: "m-2",
    md: "m-3",
    lg: "m-4",
    xl: "m-6",
  },
  mx: {
    xs: "mx-1",
    sm: "mx-2",
    md: "mx-3",
    lg: "mx-4",
    xl: "mx-6",
  },
  my: {
    xs: "my-1",
    sm: "my-2",
    md: "my-3",
    lg: "my-4",
    xl: "my-6",
  },
  mt: {
    xs: "mt-1",
    sm: "mt-2",
    md: "mt-3",
    lg: "mt-4",
    xl: "mt-6",
  },
  mb: {
    xs: "mb-1",
    sm: "mb-2",
    md: "mb-3",
    lg: "mb-4",
    xl: "mb-6",
  },
  
  // Gap mellom elementer
  gap: {
    xs: "gap-1",
    sm: "gap-2",
    md: "gap-3",
    lg: "gap-4",
    xl: "gap-6",
  },
  
  // Container spacing
  container: "p-6", // Desktop
  containerMobile: "p-4", // Mobil
  
  // Vanlige mønstre
  cardPadding: "p-6",
  cardPaddingCompact: "p-4",
  cardPaddingMobile: "p-3",
  itemSpacing: "space-y-4",
  itemSpacingTight: "space-y-2",
  itemSpacingLoose: "space-y-6",
  sectionSpacing: "mb-6",
  sectionSpacingTight: "mb-4",
  sectionSpacingLoose: "mb-8",
};

// Typografi
export const typography = {
  fontFamily: "font-['Inter',sans-serif]",
  
  // Overskriftsnivåer
  h1: "text-2xl font-semibold text-gray-900 dark:text-neutral-100",
  h2: "text-xl font-semibold text-gray-900 dark:text-neutral-100",
  h3: "text-lg font-medium text-gray-900 dark:text-neutral-100",
  h4: "text-base font-medium text-gray-900 dark:text-neutral-100",
  
  // Brødtekst
  bodyLg: "text-base text-gray-700 dark:text-neutral-300 leading-relaxed",
  body: "text-sm text-gray-600 dark:text-neutral-400",
  bodyTight: "text-sm text-gray-600 dark:text-neutral-400 leading-snug",
  
  // Mindre tekststørrelser
  small: "text-xs text-gray-500 dark:text-neutral-500",
  xsmall: "text-[0.7rem] text-gray-500 dark:text-neutral-500",
  
  // Spesielle stiler
  gradient: "bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent dark:from-blue-400 dark:to-indigo-400",
  label: "text-sm font-medium text-gray-700 dark:text-neutral-300", // For form labels
  placeholder: "text-gray-400 dark:text-neutral-600",
  error: "text-red-600 dark:text-red-400 text-sm",
  
  // Vekter
  bold: "font-bold",
  semibold: "font-semibold",
  medium: "font-medium",
  normal: "font-normal",
  
  // Justering
  center: "text-center",
  left: "text-left",
  right: "text-right",
  
  // Transformasjoner
  uppercase: "uppercase",
  capitalize: "capitalize",
  lowercase: "lowercase",
  normalcase: "normal-case",
  
  // Spesifikke bruks-stiler
  cardTitle: "font-medium text-gray-900 dark:text-neutral-100",
  cardSubtitle: "text-sm text-gray-600 dark:text-neutral-400",
  buttonText: "text-sm font-medium",
  inputText: "text-sm text-gray-800 dark:text-neutral-200",
  navigation: "text-sm font-medium",
};

// Skygger
export const shadows = {
  none: "shadow-none",
  sm: "shadow-sm",
  md: "shadow-md",
  lg: "shadow-lg",
  xl: "shadow-xl",
  inner: "shadow-inner",
};

// Visuelle effekter
export const effects = {
  glassmorphism: "backdrop-blur-sm bg-white/90 dark:bg-neutral-900/90",
  glassmorphismDense: "backdrop-blur-md bg-white/90 dark:bg-neutral-900/90",
  glassmorphismLight: "backdrop-blur-sm bg-white/70 dark:bg-neutral-900/70",
  
  // Overganger
  transition: "transition-all duration-200 ease-in-out",
  transitionFast: "transition-all duration-150 ease-in-out",
  transitionSlow: "transition-all duration-300 ease-in-out",
  
  // Hover-effekter
  hoverScale: "hover:scale-105",
  hoverSlideRight: "hover:translate-x-1",
  hoverShadow: "hover:shadow-md",
  
  // Animasjoner
  pulse: "animate-pulse",
  bounce: "animate-bounce",
  spin: "animate-spin",
  spinSlow: "animate-spin-slow", // Legg til egen animasjon i Tailwind-konfig
  gradient: "animate-gradient-x", // Legg til egen animasjon i Tailwind-konfig
  ping: "animate-ping",
  
  // Fokusringer
  focusRing: "focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:ring-offset-1",
  focusRingOffset: "focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 dark:focus:ring-blue-400",
  
  // Andre effekter
  truncate: "truncate",
  lineClamp1: "line-clamp-1",
  lineClamp2: "line-clamp-2",
  lineClamp3: "line-clamp-3",
};

// Komponentstiler
export const componentStyles = {
  // Knapper
  button: {
    base: "inline-flex items-center justify-center rounded-md transition-colors",
    primary: `bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white shadow-sm ${effects.transition}`,
    secondary: `bg-white hover:bg-gray-50 border ${colors.neutral.border} text-gray-700 dark:bg-neutral-800 dark:hover:bg-neutral-700 dark:text-neutral-200 shadow-sm ${effects.transition}`,
    danger: `${colors.danger.bg} ${colors.danger.bgHover} text-white shadow-sm ${effects.transition}`,
    ghost: `text-gray-600 hover:bg-gray-100 dark:text-neutral-400 dark:hover:bg-neutral-800 ${effects.transition}`,
    link: `text-blue-600 hover:underline dark:text-blue-400 ${effects.transition}`,
    icon: `rounded-md p-2 ${colors.neutral.bgHover} ${effects.transition}`,
    iconRound: `rounded-full p-2 ${colors.neutral.bgHover} ${effects.transition}`,
    
    // Størrelsesvarianter
    sizes: {
      xs: "h-6 text-xs px-2",
      sm: "h-8 text-sm px-3",
      md: "h-10 text-sm px-4",
      lg: "h-12 text-base px-6",
    },
    
    // Ikonstørrelser
    iconSizes: {
      xs: "h-3 w-3",
      sm: "h-4 w-4",
      md: "h-5 w-5",
      lg: "h-6 w-6",
    },
    
    // Tilstander
    states: {
      disabled: "opacity-50 cursor-not-allowed",
      loading: "opacity-75 cursor-wait",
    },
  },
  
  // Kort/Cards
  card: {
    base: `rounded-lg border ${colors.neutral.border} ${colors.neutral.bg} overflow-hidden ${effects.transition}`,
    hover: `hover:shadow-md ${effects.transition}`,
    active: `border-l-4 border-l-blue-500 bg-blue-50 dark:bg-neutral-800/60 dark:border-l-blue-400`,
    glass: `rounded-lg border ${colors.neutral.border} ${effects.glassmorphism} overflow-hidden ${effects.transition}`,
    selected: `border-blue-300 dark:border-blue-600 ring-1 ring-blue-500 dark:ring-blue-400`,
    
    // Padding-varianter
    padding: {
      none: "p-0",
      compact: "p-3",
      normal: "p-4",
      loose: "p-6",
    },
    
    // Header
    header: {
      base: "border-b border-gray-200 dark:border-neutral-800",
      compact: "px-3 py-2",
      normal: "px-4 py-3",
      loose: "px-6 py-4",
    },
    
    // Content
    content: {
      base: "",
      compact: "p-3",
      normal: "p-4",
      loose: "p-6",
    },
    
    // Footer
    footer: {
      base: "border-t border-gray-200 dark:border-neutral-800",
      compact: "px-3 py-2",
      normal: "px-4 py-3",
      loose: "px-6 py-4",
    },
  },
  
  // Input-felter
  input: {
    base: `w-full rounded-md border ${colors.neutral.border} px-3 py-2 ${colors.neutral.bg} ${colors.neutral.textPrimary} ${colors.neutral.textPlaceholder} ${effects.focusRing}`,
    disabled: "opacity-50 cursor-not-allowed",
    error: "border-red-300 dark:border-red-600 focus:ring-red-500",
    withIcon: "pl-10", // Med ikon til venstre
    
    // Størrelsesvarianter
    sizes: {
      sm: "h-8 text-sm px-2 py-1",
      md: "h-10 text-sm px-3 py-2",
      lg: "h-12 text-base px-4 py-3",
    },
    
    // Icon stiler
    icon: {
      wrapper: "absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none",
      base: "h-4 w-4 text-gray-500 dark:text-neutral-400",
    },
  },
  
  // Select/dropdowns
  select: {
    base: `w-full rounded-md border ${colors.neutral.border} px-3 py-2 ${colors.neutral.bg} ${colors.neutral.textPrimary} ${effects.focusRing}`,
    disabled: "opacity-50 cursor-not-allowed",
    triggerCompact: "h-8 px-2 text-sm",
    trigger: "h-10 px-3 text-sm",
    
    // Innhold
    content: "max-h-[200px] overflow-auto",
    item: "cursor-pointer hover:bg-gray-100 dark:hover:bg-neutral-800 px-2 py-1.5 text-sm rounded-sm",
    itemSelected: "bg-blue-50 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400",
  },
  
  // Tabeller
  table: {
    base: "w-full border-collapse",
    cell: "p-3 border-b dark:border-neutral-800",
    headerCell: "p-3 text-left border-b font-medium dark:border-neutral-800",
    row: "hover:bg-gray-50 dark:hover:bg-neutral-800/50",
    rowAlternate: "odd:bg-white even:bg-gray-50 dark:odd:bg-neutral-900 dark:even:bg-neutral-800/30",
  },
  
  // Alert/melding
  alert: {
    base: "p-4 rounded-lg border",
    info: "bg-blue-50 border-blue-200 text-blue-700 dark:bg-blue-900/20 dark:border-blue-900/50 dark:text-blue-300",
    success: "bg-green-50 border-green-200 text-green-700 dark:bg-green-900/20 dark:border-green-900/50 dark:text-green-300",
    warning: "bg-amber-50 border-amber-200 text-amber-700 dark:bg-amber-900/20 dark:border-amber-900/50 dark:text-amber-300",
    error: "bg-red-50 border-red-200 text-red-700 dark:bg-red-900/20 dark:border-red-900/50 dark:text-red-300",
  },
  
  // Badges/merker
  badge: {
    base: "inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium",
    primary: "bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300",
    secondary: "bg-gray-100 text-gray-700 dark:bg-neutral-800 dark:text-neutral-300",
    success: "bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-300",
    warning: "bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-300",
    danger: "bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-300",
  },
  
  // Sidebar
  sidebar: {
    base: "bg-white dark:bg-neutral-900 border-r border-gray-200 dark:border-neutral-800 flex flex-col",
    transition: "transition-all duration-300 ease-in-out",
    open: "w-80",
    tablet: "w-64",
    closed: "w-16",
  },
  
  // Dialog/Modal
  dialog: {
    base: "bg-white dark:bg-neutral-900 rounded-lg shadow-xl border dark:border-neutral-700",
    overlay: "bg-black/30 backdrop-blur-sm",
    header: "px-6 py-4 border-b dark:border-neutral-800",
    body: "p-6",
    footer: "px-6 py-4 border-t dark:border-neutral-800 flex justify-end space-x-3",
  },
  
  // Scroll areas
  scrollArea: {
    base: "rounded-md border bg-white dark:bg-neutral-900 dark:border-neutral-700",
    viewport: "p-4",
  },
};

// Responsive breakpoints (brukes for beregnede verdier)
export const breakpoints = {
  mobile: 640,
  smallTablet: 768,
  tablet: 960,  // Nytt mellomstørrelse breakpoint
  laptop: 1024,
  desktop: 1280,
  largeDesktop: 1536,
};

// Mediaqueries for enklere tilgang i komponenter
export const mediaQueries = {
  xs: "(max-width: 639px)",
  sm: "(min-width: 640px)",
  md: "(min-width: 768px)",
  lg: "(min-width: 1024px)",
  xl: "(min-width: 1280px)",
  "2xl": "(min-width: 1536px)",
  
  // Spesifikke segmenter
  mobileOnly: "(max-width: 639px)",
  tabletOnly: "(min-width: 640px) and (max-width: 1023px)",
  smallTabletOnly: "(min-width: 640px) and (max-width: 767px)",
  largeTabletOnly: "(min-width: 768px) and (max-width: 1023px)",
  desktopOnly: "(min-width: 1024px)",
  
  // Orienteringer
  landscape: "(orientation: landscape)",
  portrait: "(orientation: portrait)",
  mobileLandscape: "(orientation: landscape) and (max-height: 480px)",
  tabletLandscape: "(orientation: landscape) and (min-height: 481px) and (max-height: 1023px)",
};

// Konsistente layouthøyder
export const heights = {
  header: "h-16", // 64px
  headerMobile: "h-14", // 56px
  footer: "h-12", // 48px
  mobileNav: "h-16", // 64px
  sidebar: "h-screen",
  mainContent: "h-[calc(100vh-64px)]", // Totalhøyde minus header
  mainContentMobile: "h-[calc(100vh-56px)]", // Totalhøyde minus mobilheader
};

// Opprette en context for designsystemet
const DesignSystemContext = createContext({
  colors,
  spacing,
  typography,
  shadows,
  effects,
  componentStyles,
  breakpoints,
  mediaQueries,
  heights,
  isDarkMode: false,
  setIsDarkMode: () => {},
});

// Hook for enkel tilgang til designsystemet
export const useDesignSystem = () => useContext(DesignSystemContext);

// Provider-komponent
export const DesignSystemProvider = ({ children, isDarkMode, setIsDarkMode }) => {
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);
  const [isLaptop, setIsLaptop] = useState(false);
  const [isDesktop, setIsDesktop] = useState(false);
  const [isLandscape, setIsLandscape] = useState(false);

  useEffect(() => {
    // Oppdaterer skjermstørrelse state basert på vindusstørrelse
    const updateScreenSize = () => {
      const width = window.innerWidth;
      const height = window.innerHeight;
      
      setIsMobile(width < 640);
      setIsTablet(width >= 640 && width < 1024);
      setIsLaptop(width >= 1024 && width < 1280);
      setIsDesktop(width >= 1280);
      
      // Sett landskapsmodus basert på om bredden er større enn høyden,
      // men bare for mobil- og nettbrett-størrelse (under laptop)
      setIsLandscape(width > height && width < 1024);
    };

    // Initialiser
    updateScreenSize();

    // Oppdater ved endringer i vindusstørrelse
    window.addEventListener('resize', updateScreenSize);
    
    // Rydd opp
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  /*
   * Design System Context
   * ---------------------
   * Denne konteksten gir tilgang til:
   * 1. Tema-status (mørk/lys modus)
   * 2. Responsiv status (isMobile, isTablet, isLaptop, isDesktop)
   * 3. Orientering-status (isLandscape for mobil og nettbrett)
   * 
   * Landskapsmodus er spesielt viktig for mobiler og små nettbrett, 
   * hvor appen trenger spesielle justeringer for å passe med den begrensede
   * vertikale plassen men økte horisontale plassen.
   * 
   * Bruk disse verdiene for å tilpasse UI-komponenter og layout
   * basert på brukerens skjermstørrelse og orientering.
   */
  return (
    <DesignSystemContext.Provider 
      value={{
        colors,
        spacing,
        typography,
        shadows,
        effects,
        componentStyles,
        breakpoints,
        mediaQueries,
        heights,
        isDarkMode,
        setIsDarkMode,
        isMobile,
        isTablet,
        isLaptop,
        isDesktop,
        isLandscape,
      }}
    >
      {children}
    </DesignSystemContext.Provider>
  );
};

// Hjelpefunksjoner for å kombinere stiler
export const cn = (...classes) => {
  return classes.filter(Boolean).join(' ');
};

export default {
  colors,
  spacing,
  typography,
  shadows,
  effects,
  componentStyles,
  breakpoints,
  mediaQueries,
  heights,
};
